import { useEffect, useRef, useState } from 'react'

const DEFAULT_TRANSITION_DURATION = 500 // ms - matches useTemplateAnimation duration (0.5s)
const DEFAULT_DELAY_BETWEEN_TRANSITIONS = 500 // ms - delay between exit and enter animations

interface UseAnimatedTransitionOptions<T> {
  /** The target value to transition to */
  targetValue: T
  /** External visibility control */
  isVisible?: boolean
  /** Transition duration in ms (default: 500) */
  transitionDuration?: number
  /** Delay between exit animation completing and enter animation starting (default: 1000) */
  delayBetweenTransitions?: number
}

interface UseAnimatedTransitionReturn<T> {
  /** The value currently being displayed (use this for rendering) */
  displayValue: T
  /** Whether the component should be visible (use this for animation) */
  componentVisible: boolean
  /** The previous value before the current transition (useful for determining transition type) */
  previousValue: T | null
}

/**
 * Generic hook for coordinating animated transitions between any values.
 *
 * When the target value changes:
 * 1. Sets componentVisible to false (triggers exit animation)
 * 2. After transition duration, switches displayValue
 * 3. Sets componentVisible to true (triggers enter animation)
 *
 * Works with any comparable value type (strings, numbers, enums, etc.)
 *
 * @example
 * ```tsx
 * // For display state transitions
 * const { displayValue: displayState, componentVisible } = useAnimatedTransition({
 *   targetValue: targetDisplayState,
 *   isVisible: true,
 * })
 *
 * // For form factor transitions
 * const { displayValue: formFactor, componentVisible } = useAnimatedTransition({
 *   targetValue: targetFormFactor,
 *   isVisible: true,
 * })
 * ```
 */
export const useAnimatedTransition = <T>({
  targetValue,
  isVisible = true,
  transitionDuration = DEFAULT_TRANSITION_DURATION,
  delayBetweenTransitions = DEFAULT_DELAY_BETWEEN_TRANSITIONS,
}: UseAnimatedTransitionOptions<T>): UseAnimatedTransitionReturn<T> => {
  const [displayValue, setDisplayValue] = useState<T>(targetValue)
  const [componentVisible, setComponentVisible] = useState(isVisible)
  const [previousValue, setPreviousValue] = useState<T | null>(null)
  const isTransitioningRef = useRef(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const enterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timers on unmount only
  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
      if (enterTimerRef.current) clearTimeout(enterTimerRef.current)
    }
  }, [])

  // Handle value transitions with animation coordination
  useEffect(() => {
    if (targetValue !== displayValue && !isTransitioningRef.current) {
      isTransitioningRef.current = true

      // Store previous value for transition type detection
      setPreviousValue(displayValue)

      // Step 1: Hide current component (exit animation)
      setComponentVisible(false)

      // Step 2: After exit animation completes, switch value
      exitTimerRef.current = setTimeout(() => {
        setDisplayValue(targetValue)

        // Step 3: After delay, show new component (enter animation)
        enterTimerRef.current = setTimeout(() => {
          setComponentVisible(true)
          isTransitioningRef.current = false
        }, delayBetweenTransitions)
      }, transitionDuration)
    }
  }, [targetValue, displayValue, transitionDuration, delayBetweenTransitions])

  // Handle external visibility changes
  useEffect(() => {
    if (!isTransitioningRef.current) {
      setComponentVisible(isVisible)
    }
  }, [isVisible])

  return {
    displayValue,
    componentVisible,
    previousValue,
  }
}
